import clsx from 'clsx'
import {cloneDeep, isEmpty, isEqual, omit} from 'lodash'
import {FC, useCallback, useEffect, useMemo, useRef, useState} from 'react'
import {useIntl} from 'react-intl'
import {toast} from 'react-toastify'
import {ConfirmActionModal, KTSVG} from '../../../../../gori/components'
import {useCancelToken} from '../../../../../gori/hooks'
import {Button} from '../../../../../gori/widgets'
import {A1BatchService} from '../../core/requests'

type Props = {
  loadingParent: {getTemplate: boolean}
  mappingTemplate: {options: any; value: any}
  handleClickTemplate: (template: any) => void
  handleDeleteFieldSuccess: (payload: {deleteThis: boolean}) => void
}

const ButtonTemplateMapping: FC<Props> = ({
  loadingParent = {getTemplate: false},
  mappingTemplate = {options: [], value: null},
  handleClickTemplate,
  handleDeleteFieldSuccess,
}) => {
  const intl = useIntl()
  const {newCancelToken, isCancel} = useCancelToken()
  const [loading, setLoading] = useState<{delete: number | undefined}>({delete: undefined})
  const [visibleMenu, setVisibleMenu] = useState(false)
  const [valueSearch, setValueSearch] = useState('')
  const [confirmDeleteId, setConfirmDeleteId] = useState<number | undefined>(undefined)
  const dropdownRef = useRef<any>(null)

  const resetDropdown = () => setVisibleMenu(false)

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        resetDropdown()
      }
    }
    document.addEventListener('mousedown', handleClickOutside)
    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [])

  const handleDeleteField = useCallback(
    async (id) => {
      try {
        setLoading((prev) => ({...prev, delete: id}))
        await A1BatchService.deleteMappingTemplate(id, {cancelToken: newCancelToken()})
        toast.success(intl.formatMessage({id: 'DELETED_SUCCESSFULLY'}))
        handleDeleteFieldSuccess({deleteThis: mappingTemplate?.value?.id === id})
      } catch (error) {
        if (isCancel(error)) return
      } finally {
        setLoading((prev) => ({...prev, delete: undefined}))
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [intl, isCancel, newCancelToken, mappingTemplate?.value?.id]
  )

  const customsData = useMemo(() => {
    if (!valueSearch) return mappingTemplate.options
    return cloneDeep(mappingTemplate.options).filter(({label}) =>
      label.toLowerCase().includes(valueSearch.toLowerCase().trim())
    )
  }, [mappingTemplate.options, valueSearch])

  // The applied template is the one whose mapping equals the current selection, ignoring
  // the default flag.
  const isApplied = (data) =>
    isEqual(omit(mappingTemplate?.value, ['default']), omit(data, ['default']))

  const renderMenuItem = (data, index) => {
    const {label, id} = data
    return (
      <div key={index} className={clsx('dropdown-submenu', {'cursor-no-drop': loading.delete})}>
        <div
          className={clsx('dropdown-submenu__item', {
            'dropdown-submenu__item__active': isApplied(data),
            'pe-none': loading.delete,
          })}
        >
          <div
            className='flex-fill text-truncate cursor-pointer'
            onClick={() => {
              handleClickTemplate(data)
              resetDropdown()
            }}
          >
            {label}
          </div>
          {loading.delete === id ? (
            <span className='spinner-border spinner-border-sm align-middle me-2' />
          ) : (
            <Button
              event={() => setConfirmDeleteId(id)}
              className='text-hover-danger border-0 ms-2 bg-transparent rounded-circle bg-hover-white py-1'
            >
              <KTSVG
                path='/media/gori/common/delete.svg'
                className={clsx('opacity-50', {'text-white': isApplied(data)})}
                svgClassName='mh-10px'
              />
            </Button>
          )}
        </div>
      </div>
    )
  }

  return (
    <>
      {!!confirmDeleteId && (
        <ConfirmActionModal
          show={!!confirmDeleteId}
          title={intl.formatMessage({id: 'MAPPING_TEMPLATE'})}
          message={intl.formatMessage({id: 'ARE_YOU_SURE'})}
          messagePrimary={intl.formatMessage({id: 'YES'})}
          messageSecondary={intl.formatMessage({id: 'NO'})}
          handlePrimaryAction={() => {
            handleDeleteField(confirmDeleteId)
            setConfirmDeleteId(undefined)
          }}
          handleSecondaryAction={() => setConfirmDeleteId(undefined)}
          handleClose={() => setConfirmDeleteId(undefined)}
        />
      )}
      <div className='d-flex flex-fill align-items-center'>
        <label className='form-label col-form-label fw-bolder fs-5 me-4 mb-0 text-truncate'>
          {intl.formatMessage({id: 'MAPPING_TEMPLATE'})}
        </label>
        <div
          className={clsx('dropdown flex-fill position-relative', {
            'border border-2 border-primary rounded-2': mappingTemplate?.value?.label,
            'cursor-no-drop': loadingParent.getTemplate,
          })}
          ref={dropdownRef}
        >
          <div
            className={clsx('dropdown__label btn btn-sm btn-active-light-primary border w-100', {
              'pe-none': loadingParent.getTemplate,
            })}
            onClick={() => setVisibleMenu(!visibleMenu)}
          >
            {/* Not dropdown__label__value: that class draws a left separator and caps the width
                at 150px, which only makes sense when the label sits inside the box. */}
            <span className='fw-bolder fs-5 text-gray-600 text-truncate'>
              {mappingTemplate?.value?.label || intl.formatMessage({id: 'PLEASE_CHOOSE'})}
            </span>
            <i className='bi bi-caret-down-fill ms-2' />
          </div>
          <div
            className={clsx('dropdown-menu dropdown__menu start-0 end-0 mt-2', {
              show: visibleMenu,
              'pe-none': loadingParent.getTemplate,
            })}
          >
            <div className='dropdown-submenu mh-300px scroll-y'>
              <div className='px-3 py-2 sticky-top bg-white'>
                <input
                  type='text'
                  autoComplete='off'
                  className='form-control'
                  onChange={(e) => setValueSearch(e.target.value)}
                  value={valueSearch}
                />
              </div>
              {isEmpty(customsData) ? (
                <div className='text-center fw-bold fs-5 text-gray-400 py-2'>
                  {intl.formatMessage({id: 'NO_OPTIONS'})}
                </div>
              ) : (
                customsData.map((item, index) => renderMenuItem(item, index))
              )}
            </div>
          </div>
        </div>
      </div>
    </>
  )
}

export {ButtonTemplateMapping}
